import { z } from "zod/v4";
import { defineEndpoint, defineResource } from "@visioni/a3s-client-api";
import { stringToInt } from "@visioni/a3s-codecs";
import { FEATURE_NAME } from "../feature-name";

/**
 * Starts a Patriot backfill: a one-off reconciler run over a bounded range,
 * for the lanes asked for, scanned an hour at a time. Support-only, behind
 * `patriot_tools_backfill`.
 *
 * Declared here rather than imported from the reconciler so the wire contract
 * is this module's, per docs/api.practices.md. The server pins
 * {@link PATRIOT_BACKFILL_LANES} to the reconciler's own lane list at compile
 * time.
 */
export const PATRIOT_BACKFILL_LANES = [
  "activations",
  "signals",
  "operatorNotes",
  "clients",
  "actionPlans",
  "clientGroupingTypes",
  "clientGroupings",
] as const;
export type PatriotBackfillLane = (typeof PATRIOT_BACKFILL_LANES)[number];

/** Lanes with no date in Patriot's API: a backfill reads every record and ignores any range. */
export const UNDATED_PATRIOT_BACKFILL_LANES: readonly PatriotBackfillLane[] = [
  "clients",
  "actionPlans",
  "clientGroupingTypes",
  "clientGroupings",
];

export const StartPatriotBackfillRequest = z
  .object({
    lanes: z
      .array(z.enum(PATRIOT_BACKFILL_LANES))
      .nonempty()
      .refine((lanes) => new Set(lanes).size === lanes.length, "Name each lane once"),
    /** Any time, to the minute or finer. Required once any lane is dated; undated lanes ignore it. */
    start: z.iso.datetime({ offset: true }).optional(),
    /** Exclusive: a record stamped exactly at `end` is left out. Given exactly when `start` is. */
    end: z.iso.datetime({ offset: true }).optional(),
    /** Re-emit every in-range record, even one already stored and unchanged. */
    force: z.boolean().optional(),
  })
  .refine((request) => (request.start === undefined) === (request.end === undefined), {
    message: "Give both ends of the range, or neither",
    path: ["end"],
  })
  .refine(
    (request) =>
      request.start !== undefined || request.lanes.every((lane) => UNDATED_PATRIOT_BACKFILL_LANES.includes(lane)),
    { message: "Signals, activations and operator notes need a range", path: ["start"] },
  )
  .refine(
    (request) =>
      request.start === undefined || request.end === undefined || Date.parse(request.start) < Date.parse(request.end),
    { message: "The range must end after it starts", path: ["end"] },
  );
export type StartPatriotBackfillRequest = z.output<typeof StartPatriotBackfillRequest>;

export const StartPatriotBackfillResponse = z.object({
  /** When the API handed the run to the reconciler. The run itself starts moments later. */
  startedAt: z.iso.datetime(),
  backfill: StartPatriotBackfillRequest,
});
export type StartPatriotBackfillResponse = z.output<typeof StartPatriotBackfillResponse>;

/** Under `commands/`: it writes to the replica. The path's tenant plays no part. */
export const patriotBackfillResource = defineResource({
  feature: FEATURE_NAME,
  resource: "patriot/commands/backfill",
  scope: "tenant",
});

/**
 * Answers as soon as the reconciler has the run, not when it finishes: a long
 * backfill runs for hours, an hour of Patriot at a time. Its progress shows in
 * the run's `scans`.
 */
export const startPatriotBackfillEndpoint = defineEndpoint(patriotBackfillResource, {
  method: "POST",
  bodySchema: StartPatriotBackfillRequest,
  responseSchema: StartPatriotBackfillResponse,
});

/**
 * The passes a run can have, one or more per lane: activations has one for
 * tickets created in the range, and the open set, which a backfill skips.
 * `activationsCompleted` is retired, kept only so runs that started it still
 * read. Declared
 * here rather than imported; the server pins it to the reconciler's own list.
 */
export const PATRIOT_BACKFILL_SCANS = [
  "activationsCreated",
  "activationsCompleted",
  "activationsOpen",
  "signals",
  "operatorNotes",
  "clients",
  "actionPlans",
  "clientGroupingTypes",
  "clientGroupings",
] as const;
export type PatriotBackfillScanName = (typeof PATRIOT_BACKFILL_SCANS)[number];

/** How far one pass of a backfill has got. */
export const PatriotBackfillScan = z.object({
  scan: z.enum(PATRIOT_BACKFILL_SCANS),
  lane: z.enum(PATRIOT_BACKFILL_LANES),
  /** `paused` can be resumed; `stopped` is final. */
  status: z.enum(["running", "done", "failed", "paused", "stopped"]),
  /** The end of the last hour scanned. Absent for a lane with no range. */
  upTo: z.string().optional(),
  /** Where the pass stops. Absent for a lane with no range. */
  until: z.string().optional(),
  /** Records Patriot listed. */
  enumerated: z.int().nonnegative(),
  /** Records queued for copying because they were new or changed. */
  emitted: z.int().nonnegative(),
  /** Failed attempts at the pass's current hour. Above zero while running, it is retrying. */
  failures: z.int().nonnegative(),
  /** The last error, whether or not a retry got past it. */
  lastError: z.string().optional(),
});
export type PatriotBackfillScan = z.output<typeof PatriotBackfillScan>;

/** One backfill from its record: who started it, with which options, when, and how far it has got. */
export const PatriotBackfillRun = z.object({
  runId: z.string(),
  /** Absent for a run started outside Patriot tools. */
  requestedBy: z.string().optional(),
  /** The requester's address, where it could be found. */
  requestedByEmail: z.string().optional(),
  lanes: z.array(z.enum(PATRIOT_BACKFILL_LANES)),
  start: z.string().optional(),
  end: z.string().optional(),
  force: z.boolean(),
  /** When the reconciler started the run, moments after it was asked for. */
  startedAt: z.string(),
  /** One per pass. Empty for a backfill run before passes were recorded. */
  scans: z.array(PatriotBackfillScan),
});
export type PatriotBackfillRun = z.output<typeof PatriotBackfillRun>;

export const ListPatriotBackfillsQueryParams = z.object({
  limit: stringToInt.pipe(z.int().positive().max(100)).default(20),
  cursor: z.string().optional(),
});
export type ListPatriotBackfillsQueryParamsWire = z.input<typeof ListPatriotBackfillsQueryParams>;
export type ListPatriotBackfillsQueryParams = z.output<typeof ListPatriotBackfillsQueryParams>;

export const ListPatriotBackfillsResponse = z.object({
  /** Newest first. */
  items: z.array(PatriotBackfillRun),
  /** Opaque resume token; `null` when there are no older runs. */
  cursor: z.string().nullable(),
});
export type ListPatriotBackfillsResponse = z.output<typeof ListPatriotBackfillsResponse>;

/** Fleet-wide like the fleet activations: the path's tenant plays no part. */
export const patriotBackfillsResource = defineResource({
  feature: FEATURE_NAME,
  resource: "patriot/fleet/backfills",
  scope: "tenant",
});

export const listPatriotBackfillsEndpoint = defineEndpoint(patriotBackfillsResource, {
  responseSchema: ListPatriotBackfillsResponse,
  querySchema: ListPatriotBackfillsQueryParams,
});

/**
 * What can be done to a backfill once it has started. Pausing or stopping lets
 * the hour in flight finish; resuming picks each pass up where it was paused.
 * Stopping is final. The server pins this to the reconciler's own list.
 */
export const PATRIOT_BACKFILL_CONTROLS = ["pause", "resume", "stop"] as const;
export type PatriotBackfillControl = (typeof PATRIOT_BACKFILL_CONTROLS)[number];

export const ControlPatriotBackfillPathParams = z.object({ runId: z.string().min(1) });
export type ControlPatriotBackfillPathParams = z.output<typeof ControlPatriotBackfillPathParams>;

export const ControlPatriotBackfillRequest = z.object({ action: z.enum(PATRIOT_BACKFILL_CONTROLS) });
export type ControlPatriotBackfillRequest = z.output<typeof ControlPatriotBackfillRequest>;

export const ControlPatriotBackfillResponse = z.object({
  runId: z.string(),
  action: z.enum(PATRIOT_BACKFILL_CONTROLS),
  /** The passes it changed. Empty when none was in a state the action applies to. */
  changed: z.array(z.enum(PATRIOT_BACKFILL_SCANS)),
});
export type ControlPatriotBackfillResponse = z.output<typeof ControlPatriotBackfillResponse>;

/** Under `commands/` like starting one. The path's tenant plays no part. */
export const patriotBackfillRunResource = defineResource({
  feature: FEATURE_NAME,
  resource: "patriot/commands/backfill/{runId}",
  scope: "tenant",
  resourcePathSchema: ControlPatriotBackfillPathParams,
});

/** Answers once the passes have changed state: a 404 for a run that isn't a backfill. */
export const controlPatriotBackfillEndpoint = defineEndpoint(patriotBackfillRunResource, {
  method: "POST",
  bodySchema: ControlPatriotBackfillRequest,
  responseSchema: ControlPatriotBackfillResponse,
});
